/** Translations progress, shown beneath the course-generation stages. */

import { useEffect, useState } from 'react';
import { CheckCircle2, Clock, Loader2, AlertTriangle, RefreshCw, Languages } from 'lucide-react';
import toast from 'react-hot-toast';

import { getLanguageName } from '@/constants/languageOptions';
import courseTranslationService, {
  describeTranslationTiming,
  type CourseTranslationSummary,
} from '@/services/course-translation.service';

interface Props {
  courseId: string;
  /** Master language label, e.g. "English" — used in the published banner. */
  masterLanguageLabel?: string;
}

const isRunning = (row: CourseTranslationSummary) =>
  row.status === 'PENDING' || row.status === 'IN_PROGRESS';

/** Matches STALLED_AFTER_MS on the server. A run that hangs neither finishes
 *  nor fails, so nothing advances the queue and every language behind it waits. */
export const STALLED_AFTER_MS = 30 * 60 * 1000;

/** Offered optimistically: the UI only knows started_at, while the server also
 *  weighs each step's timestamps. The server is the authority and refuses with
 *  TRANSLATION_IN_PROGRESS if the run is genuinely still moving. */
export const looksStalled = (row: CourseTranslationSummary) => {
  if (!isRunning(row) || !row.started_at) return false;
  const started = new Date(row.started_at).getTime();
  return Number.isFinite(started) && Date.now() - started > STALLED_AFTER_MS;
};

export default function TranslationProgressSection({ courseId, masterLanguageLabel }: Props) {
  const [rows, setRows] = useState<CourseTranslationSummary[]>([]);
  const [pending, setPending] = useState<string[]>([]);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!courseId) return;

    let cancelled = false;

    const load = async () => {
      try {
        const { rows: list, queued } = await courseTranslationService.getTranslationsWithQueue(courseId);
        if (cancelled) return;
        setRows(list);
        setPending(queued);
        setLoaded(true);
      } catch {
        // A translations fetch failing must not break the generation page —
        // the course itself is what the admin came here for.
        if (!cancelled) setLoaded(true);
      }
    };

    load();

    // Poll while work is in flight, then keep watching for a short SETTLE
    // WINDOW before giving up.
    const SETTLE_POLLS = 4; // 4 x 4s = 16s, comfortably longer than a clone
    let settled_polls = 0;

    const timer = setInterval(() => {
      setRows((current) => {
        const busy = current.some(isRunning) || pending.length > 0;

        if (busy) {
        settled_polls = 0;
          load();
          return current;
        }

        if (settled_polls < SETTLE_POLLS) {
          settled_polls += 1;
          load();
        }

        return current;
      });
    }, 4000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [courseId, pending.length]);

  const handleRetry = async (languageCode: string) => {
    setRetrying(languageCode);
    try {
      await courseTranslationService.retryTranslation(courseId, languageCode);
      const { rows: list, queued } = await courseTranslationService.getTranslationsWithQueue(courseId);
      setRows(list);
      setPending(queued);
      toast.success(`${getLanguageName(languageCode)} restarted`);
    } catch (error) {
      // The server weighs every step's timestamp, the UI only start time. A
      // refusal here means it is genuinely still moving — say so, don't alarm.
      const message = error instanceof Error ? error.message : 'Could not restart this language';
      toast.error(
        /in progress|still being translated/i.test(message)
          ? 'This language is still making progress — leave it running.'
          : message
      );
    } finally {
      setRetrying(null);
    }
  };

  // Nothing requested — render nothing rather than an empty card. A course with
  // no translations should look exactly as it did before this feature existed.
  if (!loaded || (rows.length === 0 && pending.length === 0)) return null;

  const complete = rows.filter((r) => r.status === 'COMPLETED').length;

  return (
    <div className="mt-8">
      {/* The line that prevents the support ticket: without it, an admin sees
          translations still running and assumes nothing is usable yet. */}
      <div className="mb-4 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
        Published in {masterLanguageLabel || 'the course language'} — learners can start now.
        Translations are still being produced and appear as each one finishes.
      </div>

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Languages className="w-4 h-4 text-brand-indigo-600" />
          Translations
        </h3>
        <span className="text-xs text-gray-500">
          {complete} of {rows.length + pending.length} complete
        </span>
      </div>

      <div className="space-y-3">
        {rows.map((row) => (
          <TranslationRow
            key={row.language_code}
            row={row}
            retrying={retrying === row.language_code}
            onRetry={() => handleRetry(row.language_code)}
          />
        ))}

        {/* Queued languages have no record yet. Showing them as "queued" with a
            reason stops a not-yet-started language looking like a failed one. */}
        {pending.map((code, index) => (
          <div
            key={code}
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50/60"
          >
            <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-600 flex items-baseline gap-2">
                <span className="truncate">{getLanguageName(code)}</span>
                <span className="text-[11px] font-normal text-gray-400 flex-shrink-0">{code}</span>
              </div>
              {/* With nothing started yet, the first language waits on content
                  validation — not on a translation that does not exist. */}
              <div className="text-xs text-gray-400">
                {index === 0 && !rows.some(isRunning)
                  ? 'Starts when the content validation is done'
                  : 'Starts when the current translation finishes'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TranslationRow({
  row,
  retrying,
  onRetry,
}: {
  row: CourseTranslationSummary;
  retrying: boolean;
  onRetry: () => void;
}) {
  const { progress } = row;
  const running = isRunning(row);
  const failed = progress.failed_pieces > 0 || row.status === 'FAILED';

  const percent =
    progress.lessons_total > 0
      ? Math.round((progress.lessons_done / progress.lessons_total) * 100)
      : 0;

  // Same wording as the Translations tab, from the same function — a run
  // described two different ways on two pages reads as two different runs.
  const timing = describeTranslationTiming(row);

  return (
    <div className="px-4 py-3 rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-3">
        {row.status === 'COMPLETED' && <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
        {running && <Loader2 className="w-4 h-4 text-brand-indigo-600 animate-spin flex-shrink-0" />}
        {failed && !running && <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />}

        <div className="min-w-0 flex-1">
          {/* NAME first, code second and muted.

              This list used to print the bare code — 'bn-IN', 'mr-IN', 'ml-IN' —
              which an admin has no way to read. The code still earns its place
              beside it: it is what appears in the URL (?language=), in the
              database and in an API call, so anyone checking a run needs it.

              getLanguageName strips the flag emoji from the stored label.
              Windows has no flag glyphs and renders them as the two letters
              of the country code, so '🇮🇳 Hindi' arrives as 'IN Hindi'. */}
          <div className="text-sm font-medium text-gray-800 flex items-baseline gap-2">
            <span className="truncate">{getLanguageName(row.language_code)}</span>
            <span className="text-[11px] font-normal text-gray-400 flex-shrink-0">
              {row.language_code}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            {row.status === 'COMPLETED' && 'complete'}
            {running && 'translating'}
            {failed && !running && `${progress.failed_pieces} piece${progress.failed_pieces === 1 ? '' : 's'} failed`}
            {timing && <span className="text-gray-400"> · {timing}</span>}
          </div>
        </div>

        {failed && !running && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} />
            Retry {progress.failed_pieces}
          </button>
        )}

        {/* A hung run blocks every language queued behind it and nothing
            recovers it on its own, so it needs a way out of the UI. */}
        {looksStalled(row) && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            title="This language has been running unusually long. Restart it to release the queue."
            className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} />
            Restart
          </button>
        )}
      </div>

      {running && (
        <div className="mt-2 pl-7">
          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-brand-indigo-500 transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>
          {/* Counts, not a bare percentage — "11 of 18 lessons" is verifiable,
              "61%" is not, and an admin chasing a stuck run needs the former. */}
          <div className="mt-1 text-[11px] text-gray-500">
            {/* "pieces" while the list endpoint is the only source: it returns a
                combined count — lessons + audio + assessments — with no per-kind
                breakdown, so calling it "lessons" reported 22 for an 11-lesson
                course. Once the detail endpoint has supplied the real split
                (audio_total > 0) the precise wording is used. */}
            {progress.lessons_done} of {progress.lessons_total}{' '}
            {progress.audio_total > 0 ? 'lessons' : 'pieces'}
            {progress.total_modules > 0 &&
              ` · module ${progress.current_module} of ${progress.total_modules}`}
            {progress.audio_total > 0 && ` · audio ${progress.audio_done} of ${progress.audio_total}`}
          </div>
        </div>
      )}

      {/* A count alone says something is wrong; the list says WHAT, and the
          retry button fixes exactly those pieces rather than the whole course. */}
      {row.failed_items && row.failed_items.length > 0 && (
        <ul className="mt-2 pl-7 space-y-0.5">
          {row.failed_items.slice(0, 5).map((item, index) => (
            <li key={index} className="text-[11px] text-gray-500">
              <span className="text-gray-400">▸</span> {item.lesson}
              {item.reason ? ` — ${item.reason}` : ''}
            </li>
          ))}
          {row.failed_items.length > 5 && (
            <li className="text-[11px] text-gray-400">
              …and {row.failed_items.length - 5} more
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
